import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { LoginService } from '../login.service';

export interface AttendanceRecord {
  AutoIndex: number;
  CreationTime: string;
  CheckType: 'check-in' | 'check-out';
  IPAddress: string;
  Location?: string;
  UserName?: string;
}

export interface DailySummary {
  shifts: ShiftRecord[];
  totalHours: number;
  status: 'checked-in' | 'checked-out' | 'no_records';
}

export interface ShiftRecord {
  checkInTime: string;
  checkOutTime: string | null;
  duration: number | null;
  ipCheckIn: string;
  ipCheckOut: string | null;
}

export interface MonthlyStats {
  totalWorkHours: number;
  shiftsCount: number;
  daysWorked: number;
  totalExams: number;
  dailyExams: { [date: string]: number };
  userCategory?: string;
}

export interface AbsenceRecord {
  AutoIndex: number;
  StartDate: string;
  EndDate: string;
  AbsenceType: 'vacation' | 'sick';
  Reason?: string;
  DateCreated: string;
  CreatedBy: number;
  CreatedByName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AttendanceService {
  private apiUrl = '/api/attendance';

  constructor(
    private http: HttpClient,
    private loginService: LoginService
  ) {}

  /**
   * Check-in or Check-out
   */
  checkInOut(checkType: 'check-in' | 'check-out', latitude?: number, longitude?: number): Observable<any> {
    const body: any = { 
      checkType,
      userName: this.loginService.getUserName()
    };
    if (latitude !== undefined && longitude !== undefined) {
      body.latitude = latitude;
      body.longitude = longitude;
    }
    return this.http.post(`${this.apiUrl}/check-in-out`, body);
  }

  /**
   * Get today's attendance records
   */
  getTodayRecords(): Observable<any> {
    return this.http.get(`${this.apiUrl}/today`);
  }

  /**
   * Get daily summary for a specific date
   */
  getDailySummary(date: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/daily/${date}`);
  }

  /**
   * Get monthly report
   */
  getMonthlyReport(year: number, month: number, userId?: number): Observable<any> {
    const monthStr = String(month).padStart(2, '0');
    const url = userId 
      ? `${this.apiUrl}/monthly/${year}/${monthStr}?userId=${userId}`
      : `${this.apiUrl}/monthly/${year}/${monthStr}`;
    return this.http.get(url);
  }

  /**
   * Add missing attendance record (for admins to fix missing check-in/out)
   */
  addMissingRecord(userId: number, dateTime: string, checkType: 'check-in' | 'check-out'): Observable<any> {
    return this.http.post(`${this.apiUrl}/add-missing`, {
      userId,
      dateTime,
      checkType
    });
  }

  /**
   * Add absence record (vacation or sick leave)
   */
  addAbsence(userId: number, startDate: string, endDate: string, absenceType: 'vacation' | 'sick', reason?: string): Observable<any> {
    return this.http.post('/api/absence/add', {
      userId,
      startDate,
      endDate,
      absenceType,
      reason
    });
  }

  /**
   * Get absence records for a user
   */
  getAbsenceRecords(userId: number, startDate?: string, endDate?: string): Observable<any> {
    let url = `/api/absence/records/${userId}`;
    const params = [];
    if (startDate) params.push(`startDate=${startDate}`);
    if (endDate) params.push(`endDate=${endDate}`);
    if (params.length > 0) url += `?${params.join('&')}`;
    return this.http.get(url);
  }

  /**
   * Update absence record
   */
  updateAbsence(id: number, startDate: string, endDate: string, absenceType: 'vacation' | 'sick', reason?: string): Observable<any> {
    return this.http.put(`/api/absence/update/${id}`, {
      startDate,
      endDate,
      absenceType,
      reason
    });
  }

  /**
   * Delete absence record
   */
  deleteAbsence(id: number): Observable<any> {
    return this.http.delete(`/api/absence/delete/${id}`);
  }
}
